/**
 * Cloudinary URL helper — Sprint 76.
 *
 * `cldOptimize(url, opts)` injects delivery-time transformations into a
 * Cloudinary URL so the browser fetches a right-sized, auto-format asset.
 * Non-Cloudinary URLs (and empty inputs) pass through untouched.
 *
 * Also exports `uploadToCloudinary(file)` which POSTs an image to the
 * backend's `/admin/media/upload` endpoint.
 */
import { api } from "@/lib/api";

const CLOUDINARY_HOST = "res.cloudinary.com";
const UPLOAD_MARKER = "/upload/";
const TRANSFORM_PREFIX_RE = /^[whcqfxydgr]_/;

/**
 * @param {string|null|undefined} url
 * @param {{ width?: number, height?: number, crop?: string, quality?: string, format?: string }} [opts]
 * @returns {string|null|undefined}
 */
export function cldOptimize(url, opts = {}) {
  if (!url) return url;
  if (typeof url !== "string") return url;
  if (!url.includes(CLOUDINARY_HOST)) return url;
  const idx = url.indexOf(UPLOAD_MARKER);
  if (idx === -1) return url;
  const rest = url.slice(idx + UPLOAD_MARKER.length);
  // Skip if a transformation already exists at the head.
  const firstSegment = rest.split("/", 1)[0];
  if (firstSegment && firstSegment.split(",").some((s) => TRANSFORM_PREFIX_RE.test(s))) {
    return url;
  }
  const w = opts.width ? `w_${opts.width}` : null;
  const h = opts.height ? `h_${opts.height}` : null;
  const c = opts.crop ? `c_${opts.crop}` : null;
  const q = `q_${opts.quality || "auto"}`;
  const f = `f_${opts.format || "auto"}`;
  const parts = [w, h, c, q, f].filter(Boolean).join(",");
  return `${url.slice(0, idx + UPLOAD_MARKER.length)}${parts}/${rest}`;
}

/**
 * Upload a File / Blob to Cloudinary via the backend proxy. Returns the
 * canonical delivery URL — safe to store directly in `thumbnail_url`.
 * @param {File|Blob} file
 * @param {{ folder?: string }} [opts]
 * @returns {Promise<{ url: string, public_id?: string }>}
 */
export async function uploadToCloudinary(file, opts = {}) {
  if (!file) throw new Error("No file to upload");
  const form = new FormData();
  form.append("file", file, file.name || "upload.jpg");
  if (opts.folder) form.append("folder", opts.folder);
  const { data } = await api.post("/admin/media/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Upload a data-URL / base64 payload via the JSON endpoint (used by
 * Excel import when a row ships an inline image instead of an http URL).
 * @param {string} dataUrl
 * @param {{ folder?: string, filename?: string }} [opts]
 * @returns {Promise<{ url: string, public_id?: string }>}
 */
export async function uploadDataUrl(dataUrl, opts = {}) {
  const { data } = await api.post("/admin/media/upload-data-url", {
    data_url: dataUrl,
    folder: opts.folder,
    filename: opts.filename,
  });
  return data;
}

/** Detect any inline image payload that should be uploaded to Cloudinary. */
export function isDataUrl(value) {
  return typeof value === "string" && /^data:image\/[a-z0-9+.-]+;base64,/i.test(value);
}
